import { API } from "@/constants/api";

// Environmental flag to determine if we should use the mock API
const USE_MOCK_API = process.env.NODE_ENV === "development" &&  process.env.USE_MOCK == "TRUE";

// Detect server-side rendering
const isServer = typeof window === "undefined";

/**
 * Generic API response interface
 * Follows Interface Segregation Principle by defining a clear contract
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  message: string;
}

/**
 * API Service
 * Follows Single Responsibility Principle - handles all API communications
 */
class ApiService {
  /**
   * Generic fetch method with authentication
   * @param endpoint API endpoint
   * @param method HTTP method
   * @param body Request body
   * @returns Typed API response
   */
  private async fetchWithAuth<T>(
    endpoint: string, 
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET", 
    body?: any
  ): Promise<ApiResponse<T>> {
    // Determine the URL based on environment
    let url;
    
    if (USE_MOCK_API && endpoint === API.ENDPOINTS.MARKETPLACE_CONFIG) {
      // Use mock API for marketplace config in development when mocking is enabled
      url = `/api/mock-marketplace`;
      //process.env.NODE_ENV === "development" &&
    } else if (endpoint === API.ENDPOINTS.MARKETPLACE_CONFIG) {
      // In development, use the specific proxy for marketplace configuration
      url = `/api/marketplace-proxy`;
    } 
    // else if (process.env.NODE_ENV === "development") {
    //   // For all other endpoints in development, use the generic proxy
    //   url = `/api/proxy${endpoint}`;
    // } 
    else {
      // In production, use direct API calls
      url = `/api/proxy${endpoint}`;
      // url = `${API.BASE_URL}${endpoint}`;
    }
    
    // More visible logging for debugging
    console.log(`%c[API] Fetching from: ${url} (${USE_MOCK_API ? 'mock' : 'real'} API)`, 'background: #222; color: #bada55; padding: 3px; border-radius: 2px;');
    
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
      cache: "no-store" // No caching for dynamic data
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Generic GET request
   * @param endpoint API endpoint path
   * @returns Typed API response
   */
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    // Special case for server-side execution
    if (isServer) {
      return this.fetchServerSide<T>(endpoint, "GET");
    }
    
    return this.fetchWithAuth<T>(endpoint, "GET");
  }

  /**
   * Generic POST request
   * @param endpoint API endpoint path
   * @param body Request body
   * @returns Typed API response
   */
  async post<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    // Special case for server-side execution
    if (isServer) {
      return this.fetchServerSide<T>(endpoint, "POST", body);
    }
    
    return this.fetchWithAuth<T>(endpoint, "POST", body);
  }

  /**
   * Generic PUT request
   * @param endpoint API endpoint path
   * @param body Request body
   * @returns Typed API response
   */
  async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    // Special case for server-side execution
    if (isServer) {
      return this.fetchServerSide<T>(endpoint, "PUT", body);
    }
    
    return this.fetchWithAuth<T>(endpoint, "PUT", body);
  }

  /**
   * Generic DELETE request
   * @param endpoint API endpoint path
   * @returns Typed API response
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    // Special case for server-side execution
    if (isServer) {
      return this.fetchServerSide<T>(endpoint, "DELETE");
    }
    
    return this.fetchWithAuth<T>(endpoint, "DELETE");
  }

  /**
   * Fetches business categories from the server
   * @returns Business categories data
   */
  async getBusinessCategories<T>(): Promise<ApiResponse<T>> {
    console.log('[API] Fetching business categories');
    return this.get(`${API.ENDPOINTS.BUSINESS_CATEGORIES}`);
  }

  /**
   * Fetches the marketplace configuration from the server
   * @param forceDirect - If true, bypass the mock API and always use the real API
   * @returns Marketplace configuration
   */
  async fetchMarketplaceConfiguration<T>(forceDirect = false): Promise<ApiResponse<T>> {
    // Special handling for server-side execution
    if (isServer) {
      return this.fetchServerSide<T>(forceDirect);
    }
    if (forceDirect) {
      // When forcing direct calls in development, use proxy only for marketplace config
      const url = process.env.NODE_ENV === "development" 
        ? `/api/marketplace-proxy` 
        : `${API.BASE_URL}${API.ENDPOINTS.MARKETPLACE_CONFIG}`;
      
      console.log(`%c[API] Forcing direct call to: ${url}`, 'background: #E91E63; color: white; padding: 3px; border-radius: 2px;');
      
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marketplace_reference_id: API.MARKETPLACE_REFERENCE_ID }),
        cache: "no-store"
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`%c[API] Direct API response:`, 'background: #E91E63; color: white; padding: 3px; border-radius: 2px;', data);
      return data;
    }
    
    // Use normal fetch method (which may use mock API in development)
    return this.fetchWithAuth<T>(
      API.ENDPOINTS.MARKETPLACE_CONFIG,
      "POST",
      { marketplace_reference_id: API.MARKETPLACE_REFERENCE_ID }
    );
  }

  /**
   * Server-side marketplace config specific fetching logic
   * Handles the special case for marketplace configuration
   * @param forceDirect - If true, bypass the mock API and always use the real API
   */
  private async fetchMarketplaceServerSide<T>(forceDirect = false): Promise<ApiResponse<T>> {
    let url;
    
    // Determine the URL based on environment and forceDirect flag
    if (process.env.NODE_ENV === "production") {
      // For production, always use the real API
      url = `${API.BASE_URL}${API.ENDPOINTS.MARKETPLACE_CONFIG}`;
      console.log('[Server] Using direct API call to server.qmin.co.in for server-side rendering');
    } else if (process.env.NODE_ENV === "development" && process.env.USE_MOCK === "TRUE") {
      // In development with mock flag, use the mock API
      url = "http://localhost:3000/api/mock-marketplace";
      console.log('[Server] Using mock API for server-side rendering');
    } else {
      // In development without mock flag, use the proxy endpoint
      // This is critical - don't call server.qmin.co.in directly from server-side
      url = "http://localhost:3000/api/marketplace-proxy";
      console.log('[Server] Using marketplace-proxy API for server-side rendering');
    }
    
    console.log('[Server] Fetching marketplace config from:', url);
    
    try {
      // Use Next.js fetch with specific SSR options
      const response = await fetch(url, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "User-Agent": "NextJS-Server-Component" 
        },
        body: JSON.stringify({ marketplace_reference_id: API.MARKETPLACE_REFERENCE_ID }),
        cache: "no-store"
      });
      
      if (!response || !response.ok) {
        console.error('[Server] API error:', response?.status);
        
        // Instead of throwing, return a fallback response
        return {
          data: null as unknown as T,
          status: response?.status || 500,
          message: `API error: ${response?.status || 'unknown'}`
        };
      }
      
      try {
        const data = await response.json();
        return data;
      } catch (jsonError) {
        console.error('[Server] Error parsing JSON response:', jsonError);
        return {
          data: null as unknown as T,
          status: 500,
          message: "Failed to parse API response"
        };
      }
    } catch (error) {
      console.error('[Server] Error fetching marketplace config:', error);
      
      // Return empty response to prevent server errors
      return {
        data: null as unknown as T,
        status: 500,
        message: error instanceof Error ? error.message : "Unknown error"
      };
    }
  }

  /**
   * Server-side specific fetching logic
   * Optimized for use in Next.js server components and RSC
   * @param endpointOrForceDirect - Either the endpoint path or a boolean to force direct API calls
   * @param method - HTTP method (for generic endpoints)
   * @param body - Request body (for generic endpoints)
   */
  private async fetchServerSide<T>(
    endpointOrForceDirect: string | boolean = false,
    method: "GET" | "POST" | "PUT" | "DELETE" = "POST",
    body?: any
  ): Promise<ApiResponse<T>> {
    // Handle the legacy marketplace config special case
    if (typeof endpointOrForceDirect === "boolean") {
      return this.fetchMarketplaceServerSide<T>(endpointOrForceDirect);
    }

    // Handle generic endpoints
    const endpoint = endpointOrForceDirect;
    let url;
    
    // Determine the URL based on environment
    if (process.env.NODE_ENV === "production") {
      // In production, always use the real API
      url = `${API.BASE_URL}${endpoint}`;
      console.log(`[Server] Using direct API call to ${url}`);
    } else if (process.env.NODE_ENV === "development" && process.env.USE_MOCK === "TRUE" && endpoint === API.ENDPOINTS.MARKETPLACE_CONFIG) {
      // In development with mock flag and marketplace endpoint, use mock API
      url = "http://localhost:3000/api/mock-marketplace";
      console.log('[Server] Using mock API for server-side rendering');
    } else if (process.env.NODE_ENV === "development") {
      // In development for other endpoints, use the proxy
      url = `http://localhost:3000/api/proxy${endpoint}`;
      console.log(`[Server] Using proxied API call to ${url}`);
    } else {
      // Fallback to direct API
      url = `${API.BASE_URL}${endpoint}`;
    }
    
    console.log(`[Server] ${method} request to: ${url}`);
    
    try {
      // Use Next.js fetch with specific SSR options
      const response = await fetch(url, {
        method,
        headers: { 
          "Content-Type": "application/json",
          "User-Agent": "NextJS-Server-Component" 
        },
        body: body ? JSON.stringify(body) : undefined,
        cache: "no-store"
      });
      
      if (!response || !response.ok) {
        console.error('[Server] API error:', response?.status);
        
        // Instead of throwing, return a fallback response
        return {
          data: null as unknown as T,
          status: response?.status || 500,
          message: `API error: ${response?.status || 'unknown'}`
        };
      }
      
      try {
        const data = await response.json();
        return data;
      } catch (jsonError) {
        console.error('[Server] Error parsing JSON response:', jsonError);
        return {
          data: null as unknown as T,
          status: 500,
          message: "Failed to parse API response"
        };
      }
    } catch (error) {
      console.error('[Server] Error fetching marketplace config:', error);
      
      // Return empty response to prevent server errors
      return {
        data: null as unknown as T,
        status: 500,
        message: error instanceof Error ? error.message : "Unknown error"
      };
    }
  }
}

// Singleton pattern for API service
export const apiService = new ApiService();
